import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import GlassmorphismTokens from '../../styles/GlassmorphismTokens';
import DesignTokens from '../../styles/DesignTokens';

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: ${GlassmorphismTokens.dark.overlay};
  backdrop-filter: ${GlassmorphismTokens.blur.sm};
  -webkit-backdrop-filter: ${GlassmorphismTokens.blur.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${DesignTokens.zIndex.modal};
  padding: ${DesignTokens.spacing.lg};
`;

const ModalCard = styled(motion.div)`
  background: ${GlassmorphismTokens.glass.medium};
  backdrop-filter: ${GlassmorphismTokens.blur.lg};
  -webkit-backdrop-filter: ${GlassmorphismTokens.blur.lg};
  border: ${GlassmorphismTokens.border.medium};
  border-radius: ${DesignTokens.borderRadius['2xl']};
  max-width: 480px;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  padding: 28px;
  position: relative;
  color: white;
  box-shadow: ${GlassmorphismTokens.shadow.glassLg};

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.2); border-radius: 10px; }
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;

  @supports not (backdrop-filter: blur(1px)) {
    background: rgba(40, 40, 60, 0.85);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${DesignTokens.transition.fast};

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: white;
  }
`;

const Heading = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: white;
  margin: 0 0 4px 0;
`;

const SubHeading = styled.p`
  font-size: 13px;
  color: ${GlassmorphismTokens.dark.textSecondary};
  margin: 0 0 24px 0;
`;

const SectionTitle = styled.h3`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #60A5FA;
  margin: 0 0 16px 0;
`;

const SliderGroup = styled.div`
  margin-bottom: 20px;
`;

const SliderLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const SliderName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${GlassmorphismTokens.dark.text};
`;

const SliderValue = styled.span`
  font-size: 12px;
  color: #60A5FA;
  font-weight: 600;
  background: rgba(96, 165, 250, 0.15);
  padding: 2px 10px;
  border-radius: 12px;
`;

/* Custom glass slider built with divs for real backdrop-filter support */
const SliderContainer = styled.div`
  position: relative;
  width: 100%;
  height: 32px;
  display: flex;
  align-items: center;
  cursor: pointer;
  touch-action: none;
`;

const SliderTrackOuter = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 10px;
  border-radius: 100px;
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  overflow: hidden;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.2);
`;

const SliderFill = styled.div`
  height: 100%;
  width: ${props => props.$percent}%;
  background: linear-gradient(90deg, rgba(66, 133, 244, 0.5) 0%, rgba(96, 165, 250, 0.7) 100%);
  border-radius: 100px;
  transition: width 0.05s linear;
  box-shadow: 0 0 8px rgba(66, 133, 244, 0.3);
`;

const SliderThumb = styled.div`
  position: absolute;
  left: ${props => props.$percent}%;
  top: 50%;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1.5px solid rgba(255, 255, 255, 0.45);
  box-shadow:
    0 2px 10px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.08),
    inset 0 1px 2px rgba(255, 255, 255, 0.25);
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s, transform 0.15s;
  z-index: 1;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.65);
    box-shadow:
      0 2px 14px rgba(66, 133, 244, 0.35),
      0 0 0 4px rgba(96, 165, 250, 0.12),
      inset 0 1px 2px rgba(255, 255, 255, 0.35);
    transform: translate(-50%, -50%) scale(1.12);
  }

  &:active, &.dragging {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.7);
    box-shadow:
      0 2px 16px rgba(66, 133, 244, 0.45),
      0 0 0 6px rgba(96, 165, 250, 0.1),
      inset 0 1px 2px rgba(255, 255, 255, 0.4);
    transform: translate(-50%, -50%) scale(1.15);
  }
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 24px 0;
`;

const SaveButton = styled(motion.button)`
  width: 100%;
  padding: 14px;
  background: ${GlassmorphismTokens.gradient.blueCTA};
  color: white;
  border: none;
  border-radius: ${DesignTokens.borderRadius.lg};
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 8px;
  box-shadow: ${GlassmorphismTokens.shadow.blueCTA};
  transition: all ${DesignTokens.transition.normal};

  &:hover {
    background: ${GlassmorphismTokens.gradient.blueCTAHover};
    box-shadow: ${GlassmorphismTokens.shadow.blueCTAHover};
    transform: translateY(-1px);
  }
`;

const DEFAULT_PREFS = {
  activityDensity: 50,
  pace: 50,
  walkingTolerance: 50,
  historyCulture: 50,
  nature: 50,
  foodDining: 50,
  nightlife: 30,
};

const getLabel = (value) => {
  if (value <= 20) return 'Low';
  if (value <= 40) return 'Moderate';
  if (value <= 60) return 'Medium';
  if (value <= 80) return 'High';
  return 'Very High';
};

const SLIDERS_SETTINGS = [
  { key: 'activityDensity', name: 'Activity Density' },
  { key: 'pace', name: 'Pace' },
  { key: 'walkingTolerance', name: 'Walking Tolerance' },
];

const SLIDERS_INTERESTS = [
  { key: 'historyCulture', name: 'History & Culture' },
  { key: 'nature', name: 'Nature' },
  { key: 'foodDining', name: 'Food & Dining' },
  { key: 'nightlife', name: 'Nightlife' },
];

const UserPreferencesModal = ({ isOpen, onClose, onSave, initialPreferences }) => {
  const [prefs, setPrefs] = useState(() => ({
    ...DEFAULT_PREFS,
    ...(initialPreferences || {}),
  }));

  const handleSave = () => {
    if (onSave) onSave(prefs);
    onClose();
  };

  const handleSliderInteraction = useCallback((key, e, container) => {
    const rect = container.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const percent = Math.max(0, Math.min(100, Math.round((x / rect.width) * 100)));
    setPrefs(prev => ({ ...prev, [key]: parseInt(percent) }));
  }, []);

  const startDrag = useCallback((key, e) => {
    const container = e.currentTarget.closest('[data-slider]');
    handleSliderInteraction(key, e, container);

    const onMove = (moveEvent) => {
      moveEvent.preventDefault();
      handleSliderInteraction(key, moveEvent, container);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
  }, [handleSliderInteraction]);

  const renderSlider = ({ key, name }) => (
    <SliderGroup key={key}>
      <SliderLabel>
        <SliderName>{name}</SliderName>
        <SliderValue>{getLabel(prefs[key])}</SliderValue>
      </SliderLabel>
      <SliderContainer
        data-slider
        onMouseDown={(e) => startDrag(key, e)}
        onTouchStart={(e) => startDrag(key, e)}
      >
        <SliderTrackOuter>
          <SliderFill $percent={prefs[key]} />
        </SliderTrackOuter>
        <SliderThumb $percent={prefs[key]} />
      </SliderContainer>
    </SliderGroup>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <ModalCard
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ type: 'spring', stiffness: 260, damping: 24 }}
          >
            <CloseButton onClick={onClose}>
              <XMarkIcon style={{ width: '18px', height: '18px', color: 'currentColor' }} />
            </CloseButton>

            <Heading>Trip Preferences</Heading>
            <SubHeading>Customize how your itinerary is generated</SubHeading>

            <SectionTitle>Trip Settings</SectionTitle>
            {SLIDERS_SETTINGS.map(renderSlider)}

            <Divider />

            <SectionTitle>Interest Weights</SectionTitle>
            {SLIDERS_INTERESTS.map(renderSlider)}

            <SaveButton
              onClick={handleSave}
              whileTap={{ scale: 0.97 }}
            >
              Save Preferences
            </SaveButton>
          </ModalCard>
        </Overlay>
      )}
    </AnimatePresence>
  );
};

export default UserPreferencesModal;
